import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button, Card, Input, PageHeader, Spinner } from '../sharedComponent';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Camera, User, Lock, Save, Eye, EyeOff } from 'lucide-react';
import './Profile.css';

const Profile = () => {
  const { user, setUser } = useAuth();
  const fileRef = useRef();
  const [searchParams] = useSearchParams();

  const [profile, setProfile]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'password' ? 'password' : 'info');

  // Info form
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', bio: '' });

  // Password form
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/profile');
        setProfile(data.data);
        setForm({
          first_name: data.data.first_name || '',
          last_name:  data.data.last_name  || '',
          phone:      data.data.phone      || '',
          bio:        data.data.bio        || '',
        });
      } catch { toast.error('Failed to load profile.'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/profile', form);
      setProfile(prev => ({ ...prev, ...data.data }));
      // Update auth context name
      setUser(prev => {
        const updated = { ...prev, firstName: data.data.first_name, lastName: data.data.last_name };
        localStorage.setItem('user', JSON.stringify(updated));
        return updated;
      });
      toast.success('Profile updated successfully.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile.');
    } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('New passwords do not match.');
      return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    setPwSaving(true);
    try {
      await api.put('/profile/password', {
        current_password: pwForm.current_password,
        new_password:     pwForm.new_password,
      });
      toast.success('Password changed successfully.');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      setUser(prev => {
        const updated = { ...prev, password_change_recommended: false, password_age_days: 0 };
        localStorage.setItem('user', JSON.stringify(updated));
        return updated;
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password.');
    } finally { setPwSaving(false); }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file.'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2MB.'); return; }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const { data } = await api.put('/profile/picture', { image: ev.target.result });
        const pic = data.data.profile_picture;
        setProfile(prev => ({ ...prev, profile_picture: pic }));
        setUser(prev => {
          const updated = { ...prev, profile_picture: pic };
          localStorage.setItem('user', JSON.stringify(updated));
          return updated;
        });
        toast.success('Profile picture updated.');
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to upload picture.');
      } finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const getInitials = () => {
    const f = profile?.first_name?.[0] || '';
    const l = profile?.last_name?.[0]  || '';
    return (f + l).toUpperCase() || '?';
  };

  if (loading) return <div className="profile-loading"><Spinner size="lg" /></div>;

  return (
    <div className="profile-page">
      <PageHeader title="My Profile" subtitle="Manage your account settings and preferences" />

      <div className="profile-layout">
        {/* ── Left: Avatar card ── */}
        <div className="profile-sidebar">
          <Card className="profile-avatar-card">
            <div className="profile-avatar-wrap">
              <div className="profile-avatar">
                {profile?.profile_picture
                  ? <img src={profile.profile_picture} alt="Avatar" />
                  : <span className="profile-avatar__initials">{getInitials()}</span>
                }
                <button
                  className="profile-avatar__change"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  title="Change photo"
                >
                  {uploading ? <Spinner size="sm" /> : <Camera size={16} />}
                </button>
                <input ref={fileRef} type="file" accept="image/*"
                  style={{ display: 'none' }} onChange={handleAvatarChange} />
              </div>
            </div>

            <div className="profile-avatar-card__info">
              <h3>{profile?.first_name} {profile?.last_name}</h3>
              <p className="profile-avatar-card__email">{profile?.email}</p>
              <span className="profile-avatar-card__role">
                {profile?.role === 'admin' ? 'Administrator'
                  : profile?.role === 'internal' ? 'Internal User'
                  : 'Data Provider'}
              </span>
            </div>

            <div className="profile-avatar-card__meta">
              <div className="profile-meta-item">
                <span className="profile-meta-item__label">Username</span>
                <span className="profile-meta-item__value">@{profile?.username}</span>
              </div>
              <div className="profile-meta-item">
                <span className="profile-meta-item__label">Member since</span>
                <span className="profile-meta-item__value">
                  {new Date(profile?.created_at).toLocaleDateString('en-NA', { month: 'long', year: 'numeric' })}
                </span>
              </div>
            </div>
          </Card>
        </div>

        {/* ── Right: Tabs ── */}
        <div className="profile-content">
          {/* Tab bar */}
          <div className="profile-tabs">
            <button className={`profile-tab ${activeTab === 'info' ? 'profile-tab--active' : ''}`}
              onClick={() => setActiveTab('info')}>
              <User size={15} /> Personal Info
            </button>
            <button className={`profile-tab ${activeTab === 'password' ? 'profile-tab--active' : ''}`}
              onClick={() => setActiveTab('password')}>
              <Lock size={15} /> Change Password
            </button>
          </div>

          {/* ── Info tab ── */}
          {activeTab === 'info' && (
            <Card>
              <h4 className="profile-section__title">Personal Information</h4>
              <p className="profile-section__desc">Update your name, phone number and bio.</p>

              <div className="profile-form">
                <div className="profile-form__row">
                  <Input label="First Name" value={form.first_name}
                    onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))} required />
                  <Input label="Last Name" value={form.last_name}
                    onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))} required />
                </div>
                <Input label="Email" value={profile?.email || ''} disabled
                  hint="Email cannot be changed. Contact admin if needed." />
                <Input label="Phone Number" value={form.phone}
                  onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+264 81 000 0000" />
                <div className="profile-form__field">
                  <label className="profile-form__label">Bio</label>
                  <textarea
                    className="profile-form__textarea"
                    rows={3}
                    placeholder="Tell us a little about yourself..."
                    value={form.bio}
                    onChange={(e) => setForm(f => ({ ...f, bio: e.target.value }))}
                    maxLength={500}
                  />
                  <span className="profile-form__char-count">{form.bio.length}/500</span>
                </div>

                <div className="profile-form__actions">
                  <Button variant="primary" loading={saving} onClick={handleSaveInfo}
                    icon={<Save size={15} />}>
                    Save Changes
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* ── Password tab ── */}
          {activeTab === 'password' && (
            <Card>
              <h4 className="profile-section__title">Change Password</h4>
              <p className="profile-section__desc">Choose a strong password with at least 8 characters.</p>

              <div className="profile-form">
                {[
                  { key: 'current_password', label: 'Current Password',    hint: 'Enter the password you currently use to log in', show: showPw.current,  toggle: () => setShowPw(p => ({ ...p, current: !p.current }))  },
                  { key: 'new_password',     label: 'New Password',          hint: null, show: showPw.new,      toggle: () => setShowPw(p => ({ ...p, new: !p.new }))           },
                  { key: 'confirm_password', label: 'Confirm New Password',  hint: null, show: showPw.confirm,  toggle: () => setShowPw(p => ({ ...p, confirm: !p.confirm })) },
                ].map((a) => { const { key, label, show, toggle } = a; return (
                  <div key={key} className="profile-form__field profile-form__field--pw">
                    <label className="profile-form__label">{a.label}</label>
                    {a.hint && <span className="profile-form__hint">{a.hint}</span>}
                    <div className="profile-pw-input">
                      <input
                        type={show ? 'text' : 'password'}
                        className="profile-pw-input__field"
                        value={pwForm[key]}
                        onChange={(e) => setPwForm(p => ({ ...p, [key]: e.target.value }))}
                        placeholder={a.placeholder}
                        autoComplete={key === 'current_password' ? 'current-password' : 'new-password'}
                      />
                      <button type="button" className="profile-pw-input__toggle" onClick={toggle}>
                        {show ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {key === 'new_password' && pwForm.new_password && pwForm.new_password.length < 8 && (
                      <span className="profile-form__error">Must be at least 8 characters</span>
                    )}
                    {key === 'confirm_password' && pwForm.confirm_password && pwForm.confirm_password !== pwForm.new_password && (
                      <span className="profile-form__error">Passwords do not match</span>
                    )}
                  </div>
                ); })}

                {/* Password strength */}
                {pwForm.new_password && (
                  <div className="profile-pw-strength">
                    <div className="profile-pw-strength__bars">
                      {[1,2,3,4].map(i => {
                        const score = [
                          pwForm.new_password.length >= 8,
                          /[A-Z]/.test(pwForm.new_password),
                          /[0-9]/.test(pwForm.new_password),
                          /[^A-Za-z0-9]/.test(pwForm.new_password),
                        ].filter(Boolean).length;
                        return <div key={i} className={`profile-pw-strength__bar ${i <= score ? `profile-pw-strength__bar--${['weak','fair','good','strong'][score-1]}` : ''}`} />;
                      })}
                    </div>
                    <span className="profile-pw-strength__label">
                      {['','Weak','Fair','Good','Strong'][[
                        pwForm.new_password.length >= 8,
                        /[A-Z]/.test(pwForm.new_password),
                        /[0-9]/.test(pwForm.new_password),
                        /[^A-Za-z0-9]/.test(pwForm.new_password),
                      ].filter(Boolean).length]}
                    </span>
                  </div>
                )}

                <div className="profile-form__actions">
                  <Button variant="primary" loading={pwSaving} onClick={handleChangePassword}
                    icon={<Lock size={15} />}
                    disabled={!pwForm.current_password || !pwForm.new_password || pwForm.new_password !== pwForm.confirm_password}>
                    Change Password
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;